import React, { useState, useEffect } from 'react';
import {
  ChevronDown, Save, Gift, Users, Calendar, IndianRupee,
  Calculator, Percent, FileText, Repeat, Layers, Plus, X, Trash2, Eye
} from 'lucide-react';
import { wagesAPI, establishmentAPI, employeeAPI } from '../api/apiService';
import { notifySavedAndReload } from '../utils/notify';
import { useAuth, useActiveEstId } from './AuthContext';
import DateInput from './DateInput';
import EstablishmentCard from './EstablishmentCard';
import './UpdateEmployee.css';
import './AttendanceRegistry.css';

const BonusAllowances = () => {
  const { user } = useAuth();
  const lockedEstId = useActiveEstId();

  const [bonuses, setBonuses] = useState([]);
  const [establishments, setEstablishments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedEstablishment, setSelectedEstablishment] = useState(lockedEstId || '');

  const [formData, setFormData] = useState({
    establishment: lockedEstId || '',
    selectionType: '',
    selectedValue: '',
    bonusType: 'performance',
    otherBonusType: '',
    amount: '',
    effectiveDate: new Date().toISOString().split('T')[0],
    description: '',
    paymentMonth: '',
    recurring: false,
    calculationType: 'flat',
    bonusPercentage: ''
  });

  const bonusTypes = [
    { value: 'performance', label: 'Performance Bonus' },
    { value: 'annual', label: 'Annual Bonus' },
    { value: 'festive', label: 'Festive Bonus' },
    { value: 'signing', label: 'Signing Bonus' },
    { value: 'referral', label: 'Referral Bonus' },
    { value: 'transport', label: 'Transport Allowance' },
    { value: 'housing', label: 'Housing Allowance' },
    { value: 'meal', label: 'Meal Allowance' },
    { value: 'mobile', label: 'Mobile Allowance' },
    { value: 'other', label: 'Other Allowance' }
  ];

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // Sync when MultiClientAdmin switches org
  useEffect(() => {
    if (lockedEstId) {
      setSelectedEstablishment(lockedEstId);
      setFormData(prev => ({ ...prev, establishment: lockedEstId }));
    }
  }, [lockedEstId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (lockedEstId) {
      setLoading(false);
    } else {
      fetchEstablishments();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedEstablishment) {
      fetchBonuses();
    }
  }, [selectedEstablishment]);

  const fetchEstablishments = async () => {
    try {
      const response = await establishmentAPI.getAll();
      const estabs = response.establishments || [];
      setEstablishments(estabs);
      if (estabs.length > 0) {
        setSelectedEstablishment(estabs[0].id.toString());
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching organizations:', error);
      setLoading(false);
    }
  };

  const fetchBonuses = async () => {
    try {
      setLoading(true);
      const response = await wagesAPI.getBonuses({ establishment_id: selectedEstablishment });
      console.log('Bonuses API Response:', response);
      setBonuses(response.bonusAllowances || []);
    } catch (error) {
      console.error('Error fetching bonuses:', error);
      alert('Error loading bonuses: ' + error.message);
      setBonuses([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async (establishmentId) => {
    try {
      const response = await employeeAPI.getByEstablishment(establishmentId);
      setEmployees(response.employees || []);
    } catch (error) {
      console.error('Error fetching employees:', error);
      setEmployees([]);
    }
  };

  const fetchDepartments = async (establishmentId) => {
    try {
      const response = await employeeAPI.getByEstablishment(establishmentId);
      const emps = response.employees || [];
      const uniqueDepts = [...new Set(emps.map(emp => emp.department).filter(Boolean))];
      setDepartments(uniqueDepts);
    } catch (error) {
      console.error('Error fetching departments:', error);
      setDepartments([]);
    }
  };

  const handleEstablishmentChange = (value) => {
    setFormData(prev => ({
      ...prev,
      establishment: value,
      selectionType: '',
      selectedValue: ''
    }));
    setEmployees([]);
    setDepartments([]);
  };

  const handleSelectionTypeChange = (value) => {
    setFormData(prev => ({
      ...prev,
      selectionType: value,
      selectedValue: '',
      calculationType: 'flat',
      bonusPercentage: '',
      amount: ''
    }));

    if (value === 'employee' && formData.establishment) {
      fetchEmployees(formData.establishment);
    } else if (value === 'department' && formData.establishment) {
      fetchDepartments(formData.establishment);
    }
  };

  const handleSubmit = async () => {
    if (!formData.establishment || !formData.selectionType || !formData.bonusType) {
      alert('Please fill in all required fields');
      return;
    }

    if (formData.selectionType === 'department' && formData.calculationType === 'percentage') {
      if (!formData.bonusPercentage) {
        alert('Please enter bonus percentage');
        return;
      }
    } else {
      if (!formData.amount) {
        alert('Please enter amount');
        return;
      }
    }

    try {
      if (formData.bonusType === 'other' && !formData.otherBonusType.trim()) {
        alert('Please specify the bonus/allowance type');
        return;
      }

      const bonusData = {
        establishmentId: parseInt(formData.establishment),
        selectionType: formData.selectionType,
        employeeId: formData.selectionType === 'employee' ? formData.selectedValue : null,
        department: formData.selectionType === 'department' ? formData.selectedValue : null,
        bonusType: formData.bonusType === 'other' ? formData.otherBonusType.trim() : formData.bonusType,
        amount: formData.selectionType === 'department' && formData.calculationType === 'percentage'
          ? null
          : parseFloat(formData.amount),
        calculationType: formData.selectionType === 'department' ? formData.calculationType : 'flat',
        bonusPercentage: formData.selectionType === 'department' && formData.calculationType === 'percentage'
          ? parseFloat(formData.bonusPercentage)
          : null,
        effectiveDate: formData.effectiveDate,
        description: formData.description,
        paymentMonth: formData.paymentMonth,
        recurring: formData.recurring
      };

      await wagesAPI.addBonus(bonusData);
      notifySavedAndReload('✓ Bonus/Allowance saved successfully!');
      resetForm();
      fetchBonuses();
    } catch (error) {
      console.error('Error saving bonus/allowance:', error);
      alert('❌ Error saving bonus/allowance: ' + error.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this bonus/allowance?')) return;

    try {
      await wagesAPI.deleteBonus(id);
      notifySavedAndReload('Bonus/Allowance deleted successfully!');
      fetchBonuses();
    } catch (error) {
      console.error('Error deleting bonus/allowance:', error);
      alert('Error deleting bonus/allowance');
    }
  };

  const resetForm = () => {
    setFormData({
      establishment: lockedEstId || '',
      selectionType: '',
      selectedValue: '',
      bonusType: 'performance',
      otherBonusType: '',
      amount: '',
      effectiveDate: new Date().toISOString().split('T')[0],
      description: '',
      paymentMonth: '',
      recurring: false,
      calculationType: 'flat',
      bonusPercentage: ''
    });
    setEmployees([]);
    setDepartments([]);
    setShowModal(false);
  };

  const statusStyle = (recurring) => {
    const map = {
      recurring: { bg: '#dcfce7', color: '#15803d', border: '#86efac', label: 'Recurring' },
      onetime:   { bg: '#dbeafe', color: '#1d4ed8', border: '#93c5fd', label: 'One-time' },
    };
    const st = recurring ? map.recurring : map.onetime;
    return {
      display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
      padding: '0.25rem 0.7rem', borderRadius: '9999px',
      fontSize: '0.75rem', fontWeight: '600',
      background: st.bg, color: st.color, border: `1px solid ${st.border}`,
    };
  };

  const bonusTypeLabel = (t) => {
    const found = bonusTypes.find(bt => bt.value === t);
    return found ? found.label : t;
  };

  // ── Shared input style ───────────────────────────────────────────────
  const inputBase = {
    width: '100%', padding: '0.65rem 0.85rem',
    border: '1.5px solid var(--theme-border, #d1d5db)', borderRadius: '0.5rem',
    fontSize: '0.875rem', background: 'var(--theme-surface, #fdf9f3)',
    color: 'var(--theme-text, #111827)', transition: 'border-color 0.15s',
    boxSizing: 'border-box',
  };
  const labelBase = {
    display: 'flex', alignItems: 'center', gap: '0.3rem',
    fontSize: '0.8rem', fontWeight: '600',
    color: 'var(--theme-text, #374151)', marginBottom: '0.4rem',
  };

  return (
    <div style={{ maxWidth: '1100px' }}>

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: '1.25rem', flexWrap: 'wrap', gap: '0.75rem',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <div style={{
            width: '42px', height: '42px', borderRadius: '0.65rem',
            background: 'linear-gradient(135deg, #10b981, #059669)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 3px 8px rgba(16,185,129,0.35)',
          }}>
            <Gift size={22} color="#fff" />
          </div>
          <div>
            <h2 style={{ fontSize: '1.25rem', fontWeight: '700', color: 'var(--theme-text, #111827)', margin: 0 }}>
              Bonus & Allowances
            </h2>
            <p style={{ color: 'var(--theme-textSecondary, #6b7280)', fontSize: '0.8rem', margin: 0 }}>
              Manage employee bonuses and allowances
            </p>
          </div>
        </div>
        {selectedEstablishment && (
          <button
            onClick={() => setShowModal(true)}
            style={{
              display: 'flex', alignItems: 'center', gap: '0.45rem',
              padding: '0.55rem 1.1rem',
              background: 'linear-gradient(135deg, var(--theme-primary, #6366f1), var(--theme-primaryDark, #4f46e5))',
              color: '#fff', border: 'none', borderRadius: '0.5rem',
              fontSize: '0.85rem', fontWeight: '600', cursor: 'pointer',
              boxShadow: '0 3px 8px rgba(99,102,241,0.3)',
              transition: 'transform 0.1s, box-shadow 0.1s',
            }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 5px 14px rgba(99,102,241,0.35)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 3px 8px rgba(99,102,241,0.3)'; }}
          >
            <Plus size={16} /> Add Bonus
          </button>
        )}
      </div>

      {/* ── Establishment Selector ──────────────────────────────────────── */}
      {!lockedEstId && (
        <EstablishmentCard
          establishments={establishments}
          value={selectedEstablishment}
          onChange={(e) => setSelectedEstablishment(e.target.value)}
        />
      )}

      {/* ── Summary Cards ──────────────────────────────────────────────── */}
      {!loading && bonuses.length > 0 && (
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '0.75rem', marginBottom: '1.25rem',
        }}>
          {[
            {
              label: 'Total Amount',
              value: `₹${bonuses.reduce((s, b) => s + (parseFloat(b.amount) || 0), 0).toLocaleString('en-IN')}`,
              color: '#059669', bg: '#ecfdf5', border: '#a7f3d0',
            },
            {
              label: 'Active Bonuses',
              value: bonuses.filter(b => b.recurring).length,
              color: '#6366f1', bg: '#eef2ff', border: '#c7d2fe',
            },
            {
              label: 'Total Records',
              value: bonuses.length,
              color: '#d97706', bg: '#fffbeb', border: '#fde68a',
            },
          ].map(c => (
            <div key={c.label} style={{
              background: c.bg, border: `1px solid ${c.border}`, borderRadius: '0.65rem',
              padding: '0.85rem 1rem', display: 'flex', flexDirection: 'column', gap: '0.15rem',
            }}>
              <span style={{ fontSize: '0.72rem', fontWeight: '600', color: c.color, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                {c.label}
              </span>
              <span style={{ fontSize: '1.3rem', fontWeight: '800', color: c.color }}>
                {c.value}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* ── Modal ──────────────────────────────────────────────────────── */}
      {showModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000, padding: '1rem',
        }}
        onClick={() => resetForm()}>
          <div style={{
            background: 'var(--theme-surface, #fdf9f3)',
            borderRadius: '1rem',
            boxShadow: '0 20px 50px rgba(0,0,0,0.25)',
            width: '100%', maxWidth: '900px', maxHeight: '90vh', overflow: 'hidden',
          }}
          onClick={(e) => e.stopPropagation()}>

            {/* Modal Header */}
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '1.25rem 1.5rem',
              borderBottom: '1px solid var(--theme-border, #e5e7eb)',
              background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{
                  width: '40px', height: '40px', borderRadius: '0.5rem',
                  background: 'linear-gradient(135deg, #10b981, #059669)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: '0 3px 6px rgba(16,185,129,0.3)',
                }}>
                  <Gift size={22} color="white" />
                </div>
                <div>
                  <h3 style={{ fontSize: '1.1rem', fontWeight: '700', color: 'var(--theme-text, #1f2937)', margin: 0 }}>
                    Add Bonus/Allowance
                  </h3>
                  <p style={{ fontSize: '0.78rem', color: 'var(--theme-textSecondary, #6b7280)', margin: '0.15rem 0 0' }}>
                    Select employee/department to assign bonus
                  </p>
                </div>
              </div>
              <button onClick={() => resetForm()} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: '0.4rem', border: 'none', background: 'rgba(0,0,0,0.06)',
                borderRadius: '0.4rem', cursor: 'pointer', color: '#6b7280',
              }}>
                <X size={20} />
              </button>
            </div>

            {/* Modal Content */}
            <div style={{ padding: '1.5rem', maxHeight: 'calc(90vh - 180px)', overflowY: 'auto' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.15rem' }}>

                {/* Organization */}
                {!lockedEstId && (
                  <div>
                    <label style={labelBase}>Organization <span style={{ color: '#dc2626' }}>*</span></label>
                    <select value={formData.establishment} onChange={(e) => handleEstablishmentChange(e.target.value)}
                      style={{ ...inputBase, cursor: 'pointer' }}>
                      <option value="">Select Organization</option>
                      {establishments.map(est => <option key={est.id} value={est.id}>{est.name}</option>)}
                    </select>
                  </div>
                )}

                {/* Selection Type */}
                {(formData.establishment || lockedEstId) && (
                  <div>
                    <label style={labelBase}>Apply To <span style={{ color: '#dc2626' }}>*</span></label>
                    <select
                      value={formData.selectionType}
                      onChange={(e) => handleSelectionTypeChange(e.target.value)}
                      style={{ ...inputBase, cursor: 'pointer' }}>
                      <option value="">Select Type</option>
                      <option value="employee">Employee</option>
                      <option value="department">Department</option>
                    </select>
                  </div>
                )}

                {/* Employee Selection */}
                {formData.selectionType === 'employee' && (
                  <div>
                    <label style={labelBase}>Employee <span style={{ color: '#dc2626' }}>*</span></label>
                    <select
                      value={formData.selectedValue}
                      onChange={(e) => setFormData(prev => ({ ...prev, selectedValue: e.target.value }))}
                      style={{ ...inputBase, cursor: 'pointer' }}>
                      <option value="">Select Employee</option>
                      {employees.map(emp => (
                        <option key={emp.id} value={emp.employee_id}>
                          {emp.first_name} {emp.last_name} ({emp.employee_id}) - {emp.department || 'N/A'}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Department Selection */}
                {formData.selectionType === 'department' && (
                  <div>
                    <label style={labelBase}>Department <span style={{ color: '#dc2626' }}>*</span></label>
                    <select
                      value={formData.selectedValue}
                      onChange={(e) => setFormData(prev => ({ ...prev, selectedValue: e.target.value }))}
                      style={{ ...inputBase, cursor: 'pointer' }}>
                      <option value="">Select Department</option>
                      {departments.map((dept, index) => (
                        <option key={index} value={dept}>{dept}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Bonus Details Section */}
                {formData.selectedValue && (
                  <>
                    <div style={{ height: '1px', background: 'var(--theme-border, #e5e7eb)' }} />
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>

                      {/* Bonus Type */}
                      <div>
                        <label style={labelBase}>Bonus/Allowance Type <span style={{ color: '#dc2626' }}>*</span></label>
                        <select
                          value={formData.bonusType}
                          onChange={(e) => setFormData(prev => ({ ...prev, bonusType: e.target.value }))}
                          style={{ ...inputBase, cursor: 'pointer' }}>
                          {bonusTypes.map(type => (
                            <option key={type.value} value={type.value}>{type.label}</option>
                          ))}
                        </select>
                      </div>

                      {/* Other Bonus Type */}
                      {formData.bonusType === 'other' && (
                        <div>
                          <label style={labelBase}>Specify Other Type <span style={{ color: '#dc2626' }}>*</span></label>
                          <input
                            type="text"
                            value={formData.otherBonusType}
                            onChange={(e) => setFormData(prev => ({ ...prev, otherBonusType: e.target.value }))}
                            placeholder="Enter bonus/allowance type name"
                            style={inputBase}
                          />
                        </div>
                      )}

                      {/* Calculation Type (only for department) */}
                      {formData.selectionType === 'department' && (
                        <div>
                          <label style={labelBase}>Calculation Type</label>
                          <select
                            value={formData.calculationType}
                            onChange={(e) => setFormData(prev => ({ ...prev, calculationType: e.target.value }))}
                            style={{ ...inputBase, cursor: 'pointer' }}>
                            <option value="flat">Flat Amount</option>
                            <option value="percentage">Percentage of Basic Salary</option>
                          </select>
                        </div>
                      )}

                      {/* Amount or Percentage */}
                      {formData.selectionType === 'department' && formData.calculationType === 'percentage' ? (
                        <div>
                          <label style={labelBase}><Percent size={13} /> Bonus Percentage (%) <span style={{ color: '#dc2626' }}>*</span></label>
                          <input
                            type="number"
                            value={formData.bonusPercentage}
                            onChange={(e) => setFormData(prev => ({ ...prev, bonusPercentage: e.target.value }))}
                            placeholder="Enter percentage (e.g., 10 for 10%)"
                            min="0" max="100" step="0.01"
                            style={inputBase}
                          />
                        </div>
                      ) : (
                        <div>
                          <label style={labelBase}><IndianRupee size={13} /> Amount (Rs) <span style={{ color: '#dc2626' }}>*</span></label>
                          <input
                            type="number"
                            value={formData.amount}
                            onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                            placeholder="Enter amount"
                            min="0" step="0.01"
                            style={inputBase}
                          />
                        </div>
                      )}

                      {/* Effective Date */}
                      <div>
                        <label style={labelBase}><Calendar size={13} /> Effective Date</label>
                        <DateInput
                          value={formData.effectiveDate}
                          onChange={(e) => setFormData(prev => ({ ...prev, effectiveDate: e.target.value }))}
                          style={inputBase}
                        />
                      </div>

                      {/* Payment Month */}
                      <div>
                        <label style={labelBase}><Calendar size={13} /> Payment Month</label>
                        <select
                          value={formData.paymentMonth}
                          onChange={(e) => setFormData(prev => ({ ...prev, paymentMonth: e.target.value }))}
                          style={{ ...inputBase, cursor: 'pointer' }}>
                          <option value="">Select Month</option>
                          {months.map(month => (
                            <option key={month} value={month}>{month}</option>
                          ))}
                        </select>
                      </div>

                      {/* Recurring Checkbox */}
                      <div style={{ gridColumn: '1 / -1' }}>
                        <div style={{
                          display: 'flex', alignItems: 'center',
                          padding: '0.75rem 1rem', borderRadius: '0.5rem',
                          background: 'var(--theme-surfaceHover, #f0ebe0)',
                          border: '1px solid var(--theme-border, #e5e7eb)',
                        }}>
                          <input
                            type="checkbox"
                            id="recurring"
                            checked={formData.recurring}
                            onChange={(e) => setFormData(prev => ({ ...prev, recurring: e.target.checked }))}
                            style={{
                              width: '18px', height: '18px',
                              marginRight: '0.75rem', cursor: 'pointer',
                              accentColor: '#059669',
                            }}
                          />
                          <label htmlFor="recurring" style={{
                            fontSize: '0.85rem', fontWeight: '600',
                            color: 'var(--theme-text, #374151)',
                            cursor: 'pointer', margin: 0,
                          }}>
                            Recurring Monthly Allowance
                          </label>
                        </div>
                      </div>

                      {/* Description */}
                      <div style={{ gridColumn: '1 / -1' }}>
                        <label style={labelBase}><FileText size={13} /> Description/Notes</label>
                        <textarea
                          value={formData.description}
                          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                          rows={3}
                          placeholder="Enter any additional notes or description"
                          style={{ ...inputBase, resize: 'vertical', fontFamily: 'inherit' }}
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div style={{
              display: 'flex', justifyContent: 'flex-end', gap: '0.65rem',
              padding: '1rem 1.5rem',
              borderTop: '1px solid var(--theme-border, #e5e7eb)',
              background: 'var(--theme-surfaceHover, #f0ebe0)',
            }}>
              <button onClick={resetForm} style={{
                padding: '0.55rem 1.1rem', border: '1px solid var(--theme-border, #d1d5db)',
                background: 'var(--theme-surface, #fdf9f3)', borderRadius: '0.5rem',
                fontSize: '0.85rem', fontWeight: '600', color: 'var(--theme-textSecondary, #374151)',
                cursor: 'pointer',
              }}>
                Cancel
              </button>
              <button onClick={handleSubmit} disabled={!formData.selectedValue} style={{
                display: 'flex', alignItems: 'center', gap: '0.4rem',
                padding: '0.55rem 1.35rem',
                background: !formData.selectedValue
                  ? '#9ca3af'
                  : 'linear-gradient(135deg, var(--theme-primary, #6366f1), var(--theme-primaryDark, #4f46e5))',
                color: '#fff', border: 'none', borderRadius: '0.5rem',
                fontSize: '0.85rem', fontWeight: '600',
                cursor: !formData.selectedValue ? 'not-allowed' : 'pointer',
                boxShadow: !formData.selectedValue ? 'none' : '0 3px 8px rgba(99,102,241,0.3)',
              }}>
                <Save size={16} /> Save Bonus
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Table ──────────────────────────────────────────────────────── */}
      {selectedEstablishment && (
        <div style={{
          background: 'var(--theme-surface, #fdf9f3)',
          borderRadius: '0.75rem',
          border: '1px solid var(--theme-border, #e5e7eb)',
          overflow: 'hidden',
          boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
        }}>
          {loading ? (
            <div style={{ padding: '3rem', textAlign: 'center' }}>
              <div style={{
                width: '36px', height: '36px', margin: '0 auto 0.75rem',
                border: '3px solid var(--theme-border, #e5e7eb)',
                borderTopColor: 'var(--theme-primary, #6366f1)',
                borderRadius: '50%', animation: 'spin 0.8s linear infinite',
              }} />
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
              <p style={{ color: 'var(--theme-textSecondary)', fontSize: '0.85rem', margin: 0 }}>Loading bonuses...</p>
            </div>
          ) : bonuses.length === 0 ? (
            <div style={{ padding: '3.5rem 2rem', textAlign: 'center' }}>
              <div style={{
                width: '64px', height: '64px', margin: '0 auto 1rem', borderRadius: '50%',
                background: 'linear-gradient(135deg, #ecfdf5, #a7f3d0)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Gift size={28} style={{ color: '#059669' }} />
              </div>
              <p style={{ color: 'var(--theme-text, #374151)', fontSize: '0.95rem', fontWeight: '600', margin: '0 0 0.25rem' }}>
                No bonuses or allowances yet
              </p>
              <p style={{ color: 'var(--theme-textSecondary, #9ca3af)', fontSize: '0.82rem', margin: 0 }}>
                Click "Add Bonus" to create the first record.
              </p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{
                    background: 'linear-gradient(135deg, var(--theme-surfaceHover, #f0ebe0), var(--theme-surface, #fdf9f3))',
                    borderBottom: '2px solid var(--theme-border, #e5e7eb)',
                  }}>
                    {['Employee/Dept', 'Type', 'Amount', 'Effective Date', 'Status', 'Actions'].map(h => (
                      <th key={h} style={{
                        textAlign: h === 'Amount' ? 'right' : h === 'Actions' ? 'center' : 'left',
                        padding: '0.75rem 1rem', fontSize: '0.72rem', fontWeight: '700',
                        color: 'var(--theme-textSecondary, #6b7280)',
                        textTransform: 'uppercase', letterSpacing: '0.05em',
                      }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {bonuses.map((bonus, i) => (
                    <tr key={bonus.id} style={{
                      borderBottom: '1px solid var(--theme-border, #f0f0f0)',
                      background: i % 2 === 0 ? 'var(--theme-surface, #fdf9f3)' : 'var(--theme-surfaceHover, #f0ebe0)',
                      transition: 'background 0.1s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--theme-accent, #eef2ff)'}
                    onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? 'var(--theme-surface, #fdf9f3)' : 'var(--theme-surfaceHover, #f0ebe0)'}
                    >
                      <td style={{ padding: '0.8rem 1rem' }}>
                        <span style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--theme-text, #111827)' }}>
                          {bonus.employee_name || bonus.department}
                        </span>
                      </td>
                      <td style={{ padding: '0.8rem 1rem' }}>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
                          padding: '0.2rem 0.6rem', borderRadius: '0.35rem',
                          background: '#ecfdf5', color: '#059669', border: '1px solid #a7f3d0',
                          fontSize: '0.78rem', fontWeight: '600',
                        }}>
                          {bonusTypeLabel(bonus.bonus_type)}
                        </span>
                      </td>
                      <td style={{
                        padding: '0.8rem 1rem', textAlign: 'right',
                        fontSize: '0.9rem', fontWeight: '700', color: '#047857',
                        fontVariantNumeric: 'tabular-nums',
                      }}>
                        {bonus.calculation_type === 'percentage'
                          ? `${bonus.bonus_percentage}%`
                          : `₹${parseFloat(bonus.amount).toLocaleString('en-IN')}`}
                      </td>
                      <td style={{
                        padding: '0.8rem 1rem', fontSize: '0.84rem',
                        color: 'var(--theme-textSecondary, #6b7280)',
                      }}>
                        {bonus.effective_date || '-'}
                      </td>
                      <td style={{ padding: '0.8rem 1rem' }}>
                        <span style={statusStyle(bonus.recurring)}>
                          <span style={{
                            width: '6px', height: '6px', borderRadius: '50%',
                            background: 'currentColor', display: 'inline-block',
                          }} />
                          {bonus.recurring ? 'Recurring' : 'One-time'}
                        </span>
                      </td>
                      <td style={{ padding: '0.8rem 1rem', textAlign: 'center' }}>
                        <button onClick={() => handleDelete(bonus.id)} title="Delete" style={{
                          padding: '0.35rem', background: '#fef2f2', border: '1px solid #fecaca',
                          borderRadius: '0.4rem', cursor: 'pointer', color: '#dc2626',
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          transition: 'all 0.15s',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = '#dc2626'; e.currentTarget.style.color = '#fff'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = '#fef2f2'; e.currentTarget.style.color = '#dc2626'; }}
                        >
                          <Trash2 size={15} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BonusAllowances;
